import { makeFetchWithAuth } from "../../auth/authFetch";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { wrapFetchWithSignal } from "../../utils/abort";
import {
  BookingStatus,
  CreateOrganizationCommand,
  OrganizationDto,
  OrganizationLinkDto,
  OrganizationStatsDto,
  OrganizationsClient,
  Organization_LinksClient,
  PaginatedListOfOrganizationBookingDto,
  PaginatedListOfOrganizationDto,
  ToggleStatusRequest,
  UpdateOrganizationCommand,
} from "../../web-api-client";
import { toDateOnlyString, type DateOnlyInput } from "./organization.utils";
import { appConfig } from "../../appconfig";

export type OrganizationListParams = {
  keyword?: string | null;
  isActive?: boolean | null;
  from?: DateOnlyInput;
  to?: DateOnlyInput;
  pageNumber: number;
  pageSize: number;
};

export type OrganizationStatsParams = {
  from?: DateOnlyInput;
  to?: DateOnlyInput;
  groupBy?: string | null;
};

export type OrganizationAppointmentsParams = {
  organizationId: number;
  status?: BookingStatus | null;
  from?: DateOnlyInput;
  to?: DateOnlyInput;
  pageNumber: number;
  pageSize: number;
};

export type OrganizationExportParams = {
  organizationId: number;
  status?: BookingStatus | null;
  from?: DateOnlyInput;
  to?: DateOnlyInput;
};

export type OrganizationLinkResponse = {
  link?: string;
  token?: string;
};

export type ExportedFile = {
  blob: Blob;
  fileName: string;
};

export function createApiOrganizationsClient(signal?: AbortSignal) {
  const baseFetch = makeFetchWithAuth(fetchWithTimeout(), {
    onUnauthorized: () => {
      window.location.href = "/login";
    },
  });

  return new OrganizationsClient(appConfig.apiBaseUrl, {
    fetch: wrapFetchWithSignal(baseFetch as typeof fetch, signal),
  });
}

export function createPublicOrganizationLinksClient(signal?: AbortSignal) {
  return new Organization_LinksClient(appConfig.apiBaseUrl, {
    fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
  });
}

export async function fetchOrganizations(
  params: OrganizationListParams,
  signal?: AbortSignal
): Promise<PaginatedListOfOrganizationDto> {
  const client = createApiOrganizationsClient(signal);

  const keyword = params.keyword?.trim() ? params.keyword.trim() : undefined;
  const isActive =
    params.isActive === null || params.isActive === undefined
      ? undefined
      : params.isActive;

  // tuỳ DateOnly strategy như A/B
  const from = params.from ? new Date(params.from) : undefined;
  const to = params.to ? new Date(params.to) : undefined;

  return client.getApiOrganizations(
    keyword,
    isActive,
    false,
    from,
    to,
    params.pageNumber,
    params.pageSize
  );
}

export async function fetchOrganizationStats(
  id: number,
  params: OrganizationStatsParams,
  signal?: AbortSignal
): Promise<OrganizationStatsDto> {
  const searchParams = new URLSearchParams();

  const from = toDateOnlyString(params.from);
  const to = toDateOnlyString(params.to);
  if (from) searchParams.set("from", from);
  if (to) searchParams.set("to", to);
  if (params.groupBy) searchParams.set("groupBy", params.groupBy);

  const response = await createAdminFetch(signal)(
    `/api/organizations/${id}/stats?${searchParams.toString()}`,
    { method: "GET", headers: { Accept: "application/json" } }
  );

  return parseJsonResponse(response, OrganizationStatsDto.fromJS);
}

export async function fetchOrganizationAppointments(
  id: number,
  params: OrganizationAppointmentsParams,
  signal?: AbortSignal
): Promise<PaginatedListOfOrganizationBookingDto> {
  const searchParams = new URLSearchParams();

  searchParams.set("OrganizationId", String(params.organizationId));
  if (params.status !== undefined && params.status !== null) {
    searchParams.set("Status", String(params.status));
  }

  const from = toDateOnlyString(params.from);
  const to = toDateOnlyString(params.to);
  if (from) searchParams.set("From", from);
  if (to) searchParams.set("To", to);

  searchParams.set("PageNumber", String(params.pageNumber));
  searchParams.set("PageSize", String(params.pageSize));

  const response = await createAdminFetch(signal)(
    `/api/organizations/${id}/appointments?${searchParams.toString()}`,
    { method: "GET", headers: { Accept: "application/json" } }
  );

  return parseJsonResponse(response, PaginatedListOfOrganizationBookingDto.fromJS);
}

export async function exportOrganizationAppointments(
  id: number,
  params: OrganizationExportParams,
  signal?: AbortSignal
): Promise<ExportedFile> {
  const searchParams = new URLSearchParams();

  searchParams.set("OrganizationId", String(params.organizationId));
  if (params.status !== undefined && params.status !== null) {
    searchParams.set("Status", String(params.status));
  }

  const from = toDateOnlyString(params.from);
  const to = toDateOnlyString(params.to);
  if (from) searchParams.set("From", from);
  if (to) searchParams.set("To", to);

  const response = await createAdminFetch(signal)(
    `/api/organizations/${id}/export?${searchParams.toString()}`,
    { method: "GET" }
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || response.statusText);
  }

  const blob = await response.blob();
  const fileName = getFileNameFromResponse(response) ??
    `organization-${id}-appointments.csv`;

  return { blob, fileName };
}

export async function createOrganization(
  payload: CreateOrganizationCommand
): Promise<OrganizationDto> {
  const client = createApiOrganizationsClient();
  return client.postApiOrganizations(payload);
}

export async function updateOrganization(
  id: number,
  payload: UpdateOrganizationCommand
): Promise<OrganizationDto> {
  const client = createApiOrganizationsClient();
  return client.putApiOrganizations(id, payload);
}

export async function toggleOrganizationActive(
  id: number,
  payload: ToggleStatusRequest
): Promise<OrganizationDto> {
  const client = createApiOrganizationsClient();
  return client.patchApiOrganizationsActivate(id, payload);
}

export async function createOrganizationLink(
  id: number
): Promise<OrganizationLinkResponse> {
  const client = createApiOrganizationsClient();
  return client.postApiOrganizationsLink(id) as Promise<OrganizationLinkResponse>;
}

export async function resetOrganizationLink(
  id: number
): Promise<OrganizationLinkResponse> {
  const client = createApiOrganizationsClient();
  return client.postApiOrganizationsLinkReset(id) as Promise<OrganizationLinkResponse>;
}

export async function enableOrganizationLink(
  id: number
): Promise<OrganizationLinkDto> {
  const client = createApiOrganizationsClient();
  return client.patchApiOrganizationsLinkEnable(id);
}

export async function disableOrganizationLink(
  id: number
): Promise<OrganizationLinkDto> {
  const client = createApiOrganizationsClient();
  return client.patchApiOrganizationsLinkDisable(id);
}

export async function resolveOrganizationLink(
  token: string,
  signal?: AbortSignal
) {
  const client = createPublicOrganizationLinksClient(signal);
  return client.getApiOrganizationLinkResolve(token);
}

function createAdminFetch(signal?: AbortSignal) {
  const baseFetch = makeFetchWithAuth(fetchWithTimeout(), {
    onUnauthorized: () => {
      window.location.href = "/login";
    },
  });

  return wrapFetchWithSignal(baseFetch as typeof fetch, signal);
}

async function parseJsonResponse<T>(
  response: Response,
  parser: (data: any) => T
): Promise<T> {
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || response.statusText);
  }

  const data = responseText ? JSON.parse(responseText) : null;
  return parser(data);
}

function getFileNameFromResponse(response: Response): string | null {
  const contentDisposition = response.headers.get("content-disposition");
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const fallbackMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fallbackMatch?.[1] ?? null;
}

export type OrganizationLinkTokenResponse = {
  token?: string;
};

export async function fetchOrganizationLinkToken(
  id: number,
  signal?: AbortSignal
): Promise<OrganizationLinkTokenResponse> {
  const response = await createAdminFetch(signal)(
    `/api/organizations/${id}/link/token`,
    { method: "GET", headers: { Accept: "application/json" } }
  );

  return parseJsonResponse(response, (data) => data as OrganizationLinkTokenResponse);
}